import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";

interface RequireOnboardingProps {
  children: ReactNode;
}

/**
 * Forces users through onboarding before they can access any app route.
 * Reads from UserContext (already fetched — no extra Firestore call).
 *
 * Skips the check for:
 *   - Child accounts (created by a parent, have their own flow)
 *   - While the profile is still loading
 *
 * Race condition guard: UserContext.loading can briefly be `false` for one
 * render after Firebase auth resolves but before the Firestore profile query
 * fires. We detect this via `authUser !== null && profile === null` and hold
 * on the spinner. Login.tsx always creates a minimal profile doc on first
 * sign-in, so `profile` will never permanently be null for an authenticated user.
 */
export default function RequireOnboarding({ children }: RequireOnboardingProps) {
  const location = useLocation();
  const { profile, loading, authUser } = useUserContext();

  // Still in auth/context loading phase
  // Also hold spinner during the one-render race window where auth is done
  // but the Firestore profile snapshot hasn't arrived yet
  const profilePending = authUser !== null && profile === null;

  if (loading || profilePending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
        Loading…
      </div>
    );
  }

  // Child accounts (linked to a parent) skip the adult onboarding wizard
  const isChildAccount =
    profile?.role === "child" ||
    profile?.isChildAccount === true ||
    !!profile?.parentAccountId ||
    profile?.accountControlMode === "child";

  if (!isChildAccount) {
    const completedOnboarding =
      !!profile?.onboardingCompletedAt ||
      profile?.hasOnboarded === true;

    if (!completedOnboarding) {
      return <Navigate to="/onboarding" replace state={{ from: location }} />;
    }
  }

  return <>{children}</>;
}
